import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ProjectHeader from './ProjectHeader';
import Alert from '../../../../components/Alert/Alert';
import Loading from '../../../../components/Loading/Loading';
import api from '../../../../api';
import { AppSelect } from '../../../../components/common/select';




// Tambahkan import untuk docxtemplater
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';

// Tambahkan import untuk Buffer
import { Buffer } from 'buffer';
// atau jika menggunakan alternatif
import mammoth from 'mammoth';
import jsPDF from 'jspdf';
import { generateProjectCode, getFormatNumberForProject, updateFormatNumberCurrentNumber } from '../../api/ProjectApi';
import { createAuditProcessAlertForError, createAuditProcessCtaState } from './auditProcessUi';

export const normalizeProjectDateForApi = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const slashDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDate) {
    const [, day, month, year] = slashDate;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return text;
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Unknown error';
  if (typeof data === 'string') return data;
  return data.message || data.error || error?.message || 'Unknown error';
};


const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();


  // ✅ HELPER: Fungsi untuk cek apakah ID adalah database ID yang valid (bukan temporary ID dari Date.now())
  const isValidDatabaseId = (id) => {
    return id && typeof id === 'number' && id < 1000000000000;
  };

  // ✅ HELPER: Fungsi untuk mapping unit name/ID ke unit ID
  const mapUnitNameToId = (unitValue) => {
    if (!unitValue) {
      return '1';
    }
    
    // ✅ PERBAIKAN: Cek apakah unitOptions sudah di-load
    if (!unitOptions || unitOptions.length === 0) {
      return unitValue.toString();
    }
    
    // ✅ PERBAIKAN: Cek apakah unitValue sudah berupa ID yang valid
    const unitById = unitOptions.find(u => u.id.toString() === unitValue.toString());
    if (unitById) {
      return unitById.id.toString();
    }
    
    // ✅ PERBAIKAN: Cek apakah unitValue berupa name
    const unitByName = unitOptions.find(u => u.name === unitValue);
    if (unitByName) {
      return unitByName.id.toString();
    }
    
    // ✅ PERBAIKAN: Jika tidak ditemukan, return unitValue as-is
    return unitValue.toString();
  };

  // ✅ HELPER: Fungsi untuk mapping service name ke service ID
  const mapServiceNameToId = (serviceName) => {
    if (!serviceName) return '1';
    const service = serviceOptions.find(s => s.name === serviceName);
    return service ? service.id.toString() : '1';
  };

  // ✅ HELPER: Fungsi untuk mapping standard name ke standard ID
  const mapStandardNameToId = (standardName, serviceId) => {
    if (!standardName) return '1';
    // Cari di standardOptionsBySection berdasarkan serviceId
    const sectionIndex = proposalServices.findIndex(s => s.service === serviceId);
    if (sectionIndex >= 0 && standardOptionsBySection[sectionIndex]) {
      const standard = standardOptionsBySection[sectionIndex].find(s => s.name === standardName);
      return standard ? standard.id.toString() : '1';
    }
    return '1';
  };

  // ✅ HELPER: Fungsi untuk mapping product category name ke product category ID
  const mapProductCategoryNameToId = (categoryName, serviceId) => {
    if (!categoryName) return '1';
    // Cari di categoryOptionsBySection berdasarkan serviceId
    const sectionIndex = proposalServices.findIndex(s => s.service === serviceId);
    if (sectionIndex >= 0 && categoryOptionsBySection[sectionIndex]) {
      const category = categoryOptionsBySection[sectionIndex].find(c => c.name === categoryName);
      return category ? category.id.toString() : '1';
    }
    return '1';
  };

  // ✅ HELPER: Fungsi untuk mapping service item name ke service item ID
  const mapServiceItemNameToId = (itemName, serviceId, productCategoryId) => {
    if (!itemName) return '1';
    // Cari di itemOptionsBySection berdasarkan serviceId dan productCategoryId
    const sectionIndex = proposalServices.findIndex(s => s.service === serviceId);
    if (sectionIndex >= 0) {
      const standardIndex = proposalServices[sectionIndex].standards.findIndex(s => s.productCategory === productCategoryId);
      if (standardIndex >= 0 && itemOptionsBySection[`${sectionIndex}_${standardIndex}`]) {
        const item = itemOptionsBySection[`${sectionIndex}_${standardIndex}`].find(i => i.name === itemName);
        return item ? item.id.toString() : '1';
      }
    }
    return '1';
  };

  // ✅ HELPER: Error handler untuk menangkap JavaScript errors
  useEffect(() => {
    const handleError = (event) => {
      console.error('JavaScript Error:', event.error);
      // Prevent error from bubbling up
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // State
  const [loading, setLoading] = useState(true);
  const [productTypes, setProductTypes] = useState([]);
  const [leadProducts, setLeadProducts] = useState([]);
  const [proposalData, setProposalData] = useState({
    leadId: id, // tambahkan ini, gunakan id dari useParams
    projectDate: new Date().toISOString().split('T')[0], // ✅ PERBAIKAN: Gunakan projectDate untuk konsistensi
    validUntil: '',
    sendToClient: false,
    services: '',
    companyName: '',
    proposalCode: '',
    serviceType: '',
    // ✅ TAMBAHAN: Field untuk project
    partnerType: '',
    assignedTo: '',
    team: '',
    projectTemplateId: null,
    // ✅ TAMBAHAN: Field untuk tracking
    isProject: true, // Flag untuk membedakan proposal vs project
    multiSite: false,
    auditFlaggingType: 'none',
    integratedStandardIds: [],
    combinedStandardIds: [],
    auditConfigurationCompleted: false,
    rootStandardCount: 0,
  });
  const [alert, setAlert] = useState(null);
  const [creatingAuditProcess, setCreatingAuditProcess] = useState(false);
  const [savingSites, setSavingSites] = useState(false);
  const [auditSites, setAuditSites] = useState([]);
  const [proposalServices, setProposalServices] = useState([]);
  
  // Remove global options state
  const [standardOptionsBySection, setStandardOptionsBySection] = useState({});
  const [categoryOptionsBySection, setCategoryOptionsBySection] = useState({});
  const [itemOptionsBySection, setItemOptionsBySection] = useState({});
  
  // TAMBAHAN: Flag untuk mencegah multiple fetch
  const [dataFetched, setDataFetched] = useState(false);

  const [unitOptions, setUnitOptions] = useState([]); // Ganti dari array statis ke state
  
  // ✅ TAMBAHAN: State untuk melacak data yang dihapus
  const [deletedServices, setDeletedServices] = useState([]);
  const [deletedStandards, setDeletedStandards] = useState([]);
  const [deletedChildren, setDeletedChildren] = useState([]);

  const getAuditStandardOptions = useCallback(() => {
    return proposalServices.flatMap((section, sectionIndex) =>
      (section.standards || [])
        .filter(standard => standard?.standard || standard?.standardName)
        .map((standard, standardIndex) => ({
          id: Number(standard.id) || Number(standard.standard) || `${sectionIndex}-${standardIndex}`,
          label: standard.standardName || `Standard ${standard.standard || standardIndex + 1}`,
          serviceName: section.serviceName || 'Service',
        }))
    );
  }, [proposalServices]);

  const updateAuditStandardSelection = (targetField, standardId, checked) => {
    setProposalData(prev => {
      const current = Array.isArray(prev[targetField]) ? prev[targetField] : [];
      const normalizedId = Number(standardId);
      const next = checked
        ? Array.from(new Set([...current, normalizedId]))
        : current.filter(selectedId => Number(selectedId) !== normalizedId);
      return { ...prev, [targetField]: next };
    });
  };

  // PRD Audit #5: explicit "Create Audit Process" action.
  // The proposal-to-project conversion intentionally does NOT auto-create Audit Process.
  // Sales/authorized role must complete audit configuration (multi-site +
  // integrated/combined flagging + standards) first, then trigger this call.
  const handleCreateAuditProcess = async () => {
    if (!proposalData?.id) {
      setAlert({ type: 'warning', message: 'Save the project first before creating Audit Process.' });
      return;
    }
    if (!proposalData.auditConfigurationCompleted) {
      setAlert({
        type: 'warning',
        message: 'Audit configuration is not complete. Adjust multi-site and integrated/combined audit flags before creating Audit Process.',
      });
      return;
    }
    try {
      setCreatingAuditProcess(true);
      const response = await api.post(`/crm/projects/${proposalData.id}/audit-process/create`);
      setAlert({
        type: 'success',
        message: response?.data?.message || 'Audit Process created successfully.',
      });
    } catch (err) {
      setAlert(createAuditProcessAlertForError(err?.response?.data || {}));
    } finally {
      setCreatingAuditProcess(false);
    }
  };

  const loadProjectSites = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await api.get(`/crm/projects/${projectId}/sites`);
      setAuditSites(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.warn('Failed to load project sites', err);
      setAuditSites([]);
    }
  };

  const addAuditSite = () => {
    setAuditSites(prev => ([
      ...prev,
      {
        siteName: '',
        siteCode: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        auditMandays: 0,
        isPrimary: prev.length === 0,
        notes: '',
      },
    ]));
  };

  const updateAuditSite = (index, field, value) => {
    setAuditSites(prev => prev.map((site, siteIndex) => (
      siteIndex === index ? { ...site, [field]: value } : site
    )));
  };

  const removeAuditSite = (index) => {
    setAuditSites(prev => prev.filter((_, siteIndex) => siteIndex !== index));
  };

  const saveProjectSites = async (projectId) => {
    if (!projectId) return;
    const normalizedSites = auditSites
      .map((site, index) => ({
        ...site,
        siteName: String(site.siteName || '').trim(),
        sequence: index,
        auditMandays: Number(site.auditMandays) || 0,
        isPrimary: Boolean(site.isPrimary),
      }))
      .filter(site => site.siteName);
    try {
      setSavingSites(true);
      const response = await api.put(`/crm/projects/${projectId}/sites`, normalizedSites);
      setAuditSites(Array.isArray(response.data) ? response.data : normalizedSites);
    } finally {
      setSavingSites(false);
    }
  };

  


  const [formatNumber, setFormatNumber] = useState(null);



  // Tambahkan useEffect untuk fetch unit options
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await api.get('/crm/type-units');
        const units = (res.data || []).map(unit => ({
          id: unit.id,
          name: unit.name
        }));
        setUnitOptions(units);
        
        // ✅ PERBAIKAN: Re-map unit values setelah unitOptions di-load
        if (proposalServices.length > 0) {
          setProposalServices(prevServices => {
            return prevServices.map(section => ({
              ...section,
              standards: section.standards.map(standard => ({
                ...standard,
                unit: mapUnitNameToId(standard.unit),
                productCategories: standard.productCategories?.map(child => ({
                  ...child,
                  unit: mapUnitNameToId(child.unit)
                })) || []
              }))
            }));
          });
        }
      } catch (err) {
        console.error('Error fetching unit options:', err);
        setUnitOptions([]);
      }
    };
    fetchUnits();
  }, []);

  // ✅ PERBAIKAN: useEffect untuk re-map unit ketika unitOptions berubah
  useEffect(() => {
    if (unitOptions.length > 0 && proposalServices.length > 0) {
      setProposalServices(prevServices => {
        return prevServices.map(section => ({
          ...section,
          standards: section.standards.map(standard => ({
            ...standard,
            unit: mapUnitNameToId(standard.unit),
            productCategories: standard.productCategories?.map(child => ({
              ...child,
              unit: mapUnitNameToId(child.unit)
            })) || []
          }))
        }));
      });
    }
  }, [unitOptions, proposalServices.length]);

  const handleAddService = () => {
    setProposalServices((prev) => [
      ...prev,
      {
        id: Date.now(),
        service: '',
        standards: [
          {
            standard: '',
            productCategory: '',
            serviceItem: '',
            qty: 1,
            unit: '',
            proposeDate: '',
          },
        ],
      },
    ]);
  };

  const handleDeleteSection = (sectionIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      const deletedSection = updated[sectionIdx];
      
      // ✅ PERBAIKAN: Track deleted service jika ada ID yang valid (bukan temporary ID dari Date.now())
      if (deletedSection && isValidDatabaseId(deletedSection.id)) {
        setDeletedServices(prevDeleted => {
          const newDeleted = [...prevDeleted];
          if (!newDeleted.includes(deletedSection.id)) {
            newDeleted.push(deletedSection.id);
          }
          return newDeleted;
        });
        
        // ✅ PERBAIKAN: Track deleted standards dan children dari section ini (hanya ID yang valid)
        if (deletedSection.standards) {
          deletedSection.standards.forEach(standard => {
            if (standard && isValidDatabaseId(standard.id)) {
              setDeletedStandards(prevDeleted => {
                const newDeleted = [...prevDeleted];
                if (!newDeleted.includes(standard.id)) {
                  newDeleted.push(standard.id);
                }
                return newDeleted;
              });
              
              // Track children dari standard ini (hanya ID yang valid)
              if (standard.productCategories) {
                standard.productCategories.forEach(child => {
                  if (child && isValidDatabaseId(child.id)) {
                    setDeletedChildren(prevDeleted => {
                      const newDeleted = [...prevDeleted];
                      if (!newDeleted.includes(child.id)) {
                        newDeleted.push(child.id);
                      }
                      return newDeleted;
                    });
                  }
                });
              }
            }
          });
        }
      }
      
      updated.splice(sectionIdx, 1);
      return updated;
    });
  };

  // Example options (replace with real data as needed)
  const serviceOptions = productTypes.length > 0
    ? productTypes.map(pt => ({ 
        id: pt.id || pt.value, 
        name: pt.label || pt.name || pt.text 
      }))
    : [];


  const handleDeleteStandard = (sectionIdx, standardIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      const deletedStandard = updated[sectionIdx].standards[standardIdx];
      
      // ✅ PERBAIKAN: Track deleted standard jika ada ID yang valid (bukan temporary ID dari Date.now())
      if (deletedStandard && isValidDatabaseId(deletedStandard.id)) {
        setDeletedStandards(prevDeleted => {
          const newDeleted = [...prevDeleted];
          if (!newDeleted.includes(deletedStandard.id)) {
            newDeleted.push(deletedStandard.id);
          }
          return newDeleted;
        });
        
        // ✅ PERBAIKAN: Track deleted children dari standard ini (hanya ID yang valid)
        if (deletedStandard.productCategories) {
          deletedStandard.productCategories.forEach(child => {
            if (child && isValidDatabaseId(child.id)) {
              setDeletedChildren(prevDeleted => {
                const newDeleted = [...prevDeleted];
                if (!newDeleted.includes(child.id)) {
                  newDeleted.push(child.id);
                }
                return newDeleted;
              });
            }
          });
        }
      }
      
      const newStandards = updated[sectionIdx].standards.filter((_, i) => i !== standardIdx);
      if (newStandards.length === 0) {
        // ✅ PERBAIKAN: Track deleted service jika semua standards dihapus (hanya ID yang valid)
        const deletedSection = updated[sectionIdx];
        if (deletedSection && isValidDatabaseId(deletedSection.id)) {
          setDeletedServices(prevDeleted => {
            const newDeleted = [...prevDeleted];
            if (!newDeleted.includes(deletedSection.id)) {
              newDeleted.push(deletedSection.id);
            }
            return newDeleted;
          });
        }
        updated.splice(sectionIdx, 1);
      } else {
        updated[sectionIdx] = {
          ...updated[sectionIdx],
          standards: newStandards
        };
      }
      return updated;
    });
  };

  // Rename handleAddRow to handleAddStandard
  const handleAddStandard = (sectionIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      updated[sectionIdx] = {
        ...updated[sectionIdx],
        standards: [
          ...updated[sectionIdx].standards,
          {
            standard: '',
            productCategory: '',
            serviceItem: '',
            qty: 1,
            unit: '',
            proposeDate: '',
          }
        ]
      };
      return updated;
    });
  };

  // Rename handleAddSubRow to handleAddProductCategory
  const handleAddProductCategory = (sectionIdx, standardIdx) => {
    setProposalServices(prev => {
      return prev.map((section, sIdx) => {
        if (sIdx !== sectionIdx) return section;
        return {
          ...section,
          standards: section.standards.map((standard, stIdx) => {
            if (stIdx !== standardIdx) return standard;
            return {
              ...standard,
              productCategories: [
                ...(standard.productCategories || []),
                {
                  // ✅ FIX: Include standard from parent for proper saving
                  standard: standard.standard || '',
                  standardName: standard.standardName || 'Standard',
                  productCategory: '',
                  serviceItem: '',
                  qty: 1,
                  unit: '',
                  proposeDate: '',
                }
              ]
            };
          })
        };
      });
    });
  };

  // Delete sub-row (child)
  const handleDeleteProductCategory = (sectionIdx, standardIdx, productCategoryIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      const standard = updated[sectionIdx].standards[standardIdx];
      const deletedChild = standard.productCategories[productCategoryIdx];
      
      // ✅ PERBAIKAN: Track deleted child jika ada ID yang valid (bukan temporary ID dari Date.now())
      if (deletedChild && isValidDatabaseId(deletedChild.id)) {
        setDeletedChildren(prevDeleted => {
          const newDeleted = [...prevDeleted];
          if (!newDeleted.includes(deletedChild.id)) {
            newDeleted.push(deletedChild.id);
          }
          return newDeleted;
        });
      }
      
      const newProductCategories = standard.productCategories.filter((_, i) => i !== productCategoryIdx);
      updated[sectionIdx].standards[standardIdx] = { ...standard, productCategories: newProductCategories };
      return updated;
    });
  };

  // Tambahkan DUMMY_DATA setelah state declarations
  // const DUMMY_DATA = { ... };

  // ✅ PERBAIKAN: Tambahkan fungsi untuk fetch template data dari API untuk project
  const fetchTemplateData = async (projectId) => {
    try {
      const response = await api.get(`/crm/projects/${projectId}/template-dummy-data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project template data:', error);
      // Fallback ke dummy data jika API gagal
      return {
        company_name: 'PT Dummy Client Indonesia',
        company_address: 'Jl. Sudirman No. 123, Jakarta Selatan 12190',
        company_pic: 'Yadi Mulyadi',
        standard: 'ISO 9001:2015',
        standard_scheme: 'Quality Management System',
        scope: 'Manufacturing of Electronic Components',
        project_number: 'PROJ-2024-001',
        project_date: '2024-06-01',
        project_name: 'ISO 9001:2015 Certification Project',
        pic_name: 'Yadi Mulyadi',
        pic_phone: '+62 812-3456-7890',
        pic_email: 'yadi.mulyadi@dummyclient.com',
        service_items: [],
        years: [],
        grand_total: '0'
      };
    }
  };

  // ✅ PERBAIKAN: Tambahkan fungsi untuk download dan fill template untuk project
  const downloadAndFillTemplate = async (templateId, projectId) => {
    try {
      // Download template dari API untuk project
      const response = await api.get(`/crm/project-templates/${templateId}/download-template`, {
        responseType: 'blob'
      });
      
      // Convert response ke File object
      const file = new File([response.data], 'project_template.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      // Gunakan fungsi fillDocxWithDummyData yang sudah diupdate
      const filledDocxBlob = await fillDocxWithDummyData(file, projectId);
      
      return filledDocxBlob; // Return blob untuk diproses lebih lanjut
    } catch (error) {
      console.error('Error downloading project template:', error);
      alert('Failed to download project template');
      throw error; // Throw error untuk di-catch di fungsi handleSave
    }
  };

  const downloadAndFillTemplateBase64 = async (templateId, projectId) => {
    try {
      const response = await api.get(`/crm/project-templates/${templateId}/template-base64`);
      
      // Convert base64 ke blob
      const byteCharacters = atob(response.data.fileContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { 
        type: response.data.mimeType 
      });
      
      // Convert ke File object
      const file = new File([blob], response.data.fileName, {
        type: response.data.mimeType
      });
      
      await fillDocxWithDummyData(file, projectId);
      
    } catch (error) {
      console.error('Error downloading project template:', error);
      alert('Failed to download project template');
    }
  };

  // ✅ PERBAIKAN: Fungsi untuk fill docx dengan data dari API untuk project
  const fillDocxWithDummyData = async (file, projectId) => {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new PizZip(arrayBuffer);
    const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });
    
    // Fetch data dari API untuk project
    const templateData = await fetchTemplateData(projectId);
    doc.setData(templateData);
    
    try {
      doc.render();
    } catch (error) {
      alert('Error filling project template: ' + error.message);
      return;
    }
    const out = doc.getZip().generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    
    // HAPUS download file untuk user - tidak diperlukan lagi
    // saveAs(out, 'filled_project_template.docx');
    
    return out; // Return blob untuk diproses lebih lanjut
  };

  // Tambahkan fungsi untuk generate proposal code
  const generateProjectCodeFromFormat = async () => {
    try {
      // 1. Ambil format number untuk project
      const formatNumberData = await getFormatNumberForProject();
      if (!formatNumberData) {
        throw new Error('Format number untuk project tidak ditemukan');
      }
      
      // 2. Generate project code dari backend
      const generatedCode = await generateProjectCode();
      
      // 3. Update project data dengan code yang di-generate
      setProposalData(prev => ({
        ...prev,
        proposalCode: generatedCode
      }));
      
      // 4. Update current number di format number
      if (formatNumberData.id) {
        await updateFormatNumberCurrentNumber(formatNumberData.id, formatNumberData.currentNumber + 1);
      }
      
      return generatedCode;
    } catch (error) {
      console.error('Error generating project code:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to generate project code: ' + error.message
      });
      return null;
    }
  };

  // ✅ PERBAIKAN: Fungsi handleSave untuk Project (bukan Proposal)
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // ✅ VALIDASI: Pastikan data yang diperlukan ada
      if (!proposalData.companyName) {
        setAlert({ type: 'warning', message: 'Company name is required' });
        setLoading(false);
        return;
      }
      
      if (!proposalData.projectDate) {
        setAlert({ type: 'warning', message: 'Project date is required' });
        setLoading(false);
        return;
      }
      
      if (!proposalData.validUntil) {
        setAlert({ type: 'warning', message: 'Valid until date is required' });
        setLoading(false);
        return;
      }
      
      // ✅ VALIDASI: Pastikan ada services yang diisi
      const hasValidServices = proposalServices.some(section => 
        section.standards.some(standard => 
          standard.standard || standard.productCategory || standard.serviceItem
        )
      );
      
      if (!hasValidServices) {
        setAlert({ type: 'warning', message: 'At least one service item is required' });
        setLoading(false);
        return;
      }
      
      // ✅ PERBAIKAN: Tidak perlu hitung grandTotal karena project tidak ada hubungannya dengan uang
      // let grandTotal = 0;
      // proposalServices.forEach(section => {
      //   section.standards.forEach(standard => {
      //     // Hitung total dari parent standard
      //     if (standard.unitPrice && standard.qty) {
      //       grandTotal += (standard.unitPrice * standard.qty);
      //     }
      //     
      //     // Hitung total dari children (sub-services)
      //     if (standard.productCategories) {
      //       standard.productCategories.forEach(child => {
      //         if (child.unitPrice && child.qty) {
      //           grandTotal += (child.unitPrice * child.qty);
      //         }
      //       });
      //     }
      //   });
      // });

      // ✅ PERBAIKAN: Build payload sesuai struktur ProjectDTO yang baru
      const payload = {
        leadId: parseInt(id),
        companyName: proposalData.companyName,
        projectDate: normalizeProjectDateForApi(proposalData.projectDate),
        validUntil: normalizeProjectDateForApi(proposalData.validUntil),
        partnerType: proposalData.partnerType || null,
        // ✅ PERBAIKAN: Tidak perlu kirim grandTotal karena project tidak ada hubungannya dengan uang
        // grandTotal: grandTotal,
        // ✅ PERBAIKAN: Jangan hardcode status, biarkan backend yang menentukan
        // statusDesc: 'Draft',  // ❌ HAPUS: Jangan hardcode status
        // status: 1,           // ❌ HAPUS: Jangan hardcode status
        projectTemplateId: proposalData.projectTemplateId || null,
        assignedTo: proposalData.assignedTo || null,
        team: proposalData.team || null,
        multiSite: Boolean(proposalData.multiSite),
        auditFlaggingType: proposalData.auditFlaggingType || 'none',
        auditMethodology: proposalData.auditMethodology || null,
        integratedStandardIds: proposalData.auditFlaggingType === 'integrated' ? proposalData.integratedStandardIds : [],
        combinedStandardIds: proposalData.auditFlaggingType === 'combined' ? proposalData.combinedStandardIds : [],
        // ✅ PERBAIKAN: Project services mapping sesuai backend dengan ID sebagai number
        projectServices: proposalServices.map((section, idx) => ({
          id: section.id || null, // ✅ TAMBAHAN: Include ID untuk update/delete tracking
          serviceId: parseInt(section.service) || 1, // Convert ke number
          serviceName: section.serviceName || 'Service',
          standards: section.standards
            .filter(std => std.standard || std.productCategory || std.serviceItem || std.proposeDate)
            .map((std, standardIdx) => ({
              id: std.id || null, // ✅ TAMBAHAN: Include ID untuk update/delete tracking
              standardId: parseInt(std.standard) || 1, // Convert ke number
              standardName: std.standardName || 'Standard',
              productCategoryId: parseInt(std.productCategory) || 1, // Convert ke number
              productCategoryName: std.productCategoryName || 'Product Category',
              serviceItemId: parseInt(std.serviceItem) || 1, // Convert ke number
              serviceItemName: std.serviceItemName || 'Service Item',
              yearType: std.yearType || 0,
              selectedYear: std.selectedYear || new Date().getFullYear(),
              qty: parseInt(std.qty) || 1,
              unit: std.unit,
              workflowIdSystem: std.workflowIdSystem || null,
              workflowIdChoice: std.workflowIdChoice || null,
              proposeDate: normalizeProjectDateForApi(std.proposeDate), // ✅ TAMBAHAN: Include proposeDate in payload
              // ✅ PERBAIKAN: Children mapping untuk sub-services dengan ID sebagai number
              children: std.productCategories
                ? std.productCategories
                    .filter(pc => pc.productCategory || pc.serviceItem || pc.qty) // ✅ FIX: Check for meaningful data, not proposeDate
                    .map(pc => ({
                      id: pc.id || null, // ✅ TAMBAHAN: Include ID untuk update/delete tracking
                      standardId: parseInt(std.standard) || parseInt(pc.standard) || 1, // ✅ FIX: Use parent's standardId, fallback to child's standard
                      standardName: std.standardName || pc.standardName || 'Standard', // ✅ FIX: Use parent's standardName
                      productCategoryId: parseInt(pc.productCategory) || 1, // Convert ke number
                      productCategoryName: (() => {
                        // ✅ FIX: Get name from options if available, otherwise use stored name
                        const categoryOption = categoryOptionsBySection[idx]?.find(opt => opt.id === pc.productCategory);
                        return categoryOption?.name || pc.productCategoryName || 'Product Category';
                      })(),
                      serviceItemId: parseInt(pc.serviceItem) || 1, // Convert ke number
                      serviceItemName: (() => {
                        // ✅ FIX: Get name from options if available, otherwise use stored name
                        const itemOption = itemOptionsBySection[`${idx}_${standardIdx}`]?.find(opt => opt.id === pc.serviceItem);
                        return itemOption?.name || pc.serviceItemName || 'Service Item';
                      })(),
                      yearType: pc.yearType || 0,
                      selectedYear: pc.selectedYear || new Date().getFullYear(),
                      qty: parseInt(pc.qty) || 1,
                      unit: pc.unit,
                      workflowIdSystem: pc.workflowIdSystem || null,
                      workflowIdChoice: pc.workflowIdChoice || null,
                      proposeDate: normalizeProjectDateForApi(pc.proposeDate) // ✅ TAMBAHAN: Include proposeDate for children
                    }))
                : []
            }))
        })),
        // ✅ TAMBAHAN: Include deleted services information for backend
        deletedServices: deletedServices,
        deletedStandards: deletedStandards,
        deletedChildren: deletedChildren
      };


      let savedProjectId = null;
      
      // ✅ PERBAIKAN: Cek apakah project sudah ada untuk lead ini
      let existingProjectId = null;
      try {
        const existsRes = await api.get(`/crm/projects/detail/lead/${id}`);
        existingProjectId = existsRes.data && existsRes.data.id ? existsRes.data.id : null;
      } catch (lookupError) {
        const status = lookupError?.response?.status;
        if (status && status !== 404) {
          throw lookupError;
        }
      }
        
      if (existingProjectId) {
        // PUT (update) - update project yang sudah ada
        const updateResponse = await api.put(`/crm/projects/${existingProjectId}`, payload);
        savedProjectId = existingProjectId;
          
          // ✅ Update local data dengan response dari backend
          if (updateResponse.data) {
            setProposalData(prev => ({
              ...prev,
              id: updateResponse.data.id || existingProjectId,
              proposalCode: updateResponse.data.projectCode || prev.proposalCode,
              companyName: updateResponse.data.companyName || prev.companyName,
              projectDate: updateResponse.data.projectDate || prev.projectDate,
              validUntil: updateResponse.data.validUntil || prev.validUntil,
              multiSite: Boolean(updateResponse.data.multiSite),
              auditFlaggingType: updateResponse.data.auditFlaggingType || 'none',
              auditMethodology: updateResponse.data.auditMethodology || '',
              integratedStandardIds: updateResponse.data.integratedStandardIds || [],
              combinedStandardIds: updateResponse.data.combinedStandardIds || [],
              auditConfigurationCompleted: Boolean(updateResponse.data.auditConfigurationCompleted),
              rootStandardCount: updateResponse.data.rootStandardCount || 0,
            }));
            loadProjectSites(updateResponse.data.id || existingProjectId);
            
            // ✅ TAMBAHAN: Update local services dengan data dari backend untuk sync
            if (updateResponse.data.projectServices) {
              const updatedServices = updateResponse.data.projectServices.map((service, sectionIndex) => ({
                id: service.id || Date.now(),
                service: service.serviceId?.toString() || (sectionIndex === 0 ? '1' : '2'),
                serviceName: service.serviceName || 'Service',
                standards: (service.standards || [])
                  .filter(standard => standard)
                  .map(standard => ({
                    id: standard.id,
                    standard: standard.standardId?.toString() || '1',
                    standardName: standard.standardName || 'Standard',
                    productCategory: standard.productCategoryId?.toString() || '1',
                    productCategoryName: standard.productCategoryName || 'Product Category',
                    serviceItem: standard.serviceItemId?.toString() || '1',
                    serviceItemName: standard.serviceItemName || 'Service Item',
                    yearType: standard.yearType || 0,
                    selectedYear: standard.selectedYear || new Date().getFullYear(),
                    qty: standard.qty || 1,
                    unit: standard.unit || '1',
                    proposeDate: standard.proposeDate || '',
                    workflowIdSystem: standard.workflowIdSystem || null,
                    workflowIdChoice: standard.workflowIdChoice || null,
                    // Map children (sub-services)
                    productCategories: (standard.children || [])
                      .filter(child => child)
                      .map(child => ({
                        id: child.id,
                        standard: child.standardId?.toString() || standard.standardId?.toString() || '1', // ✅ FIX: Include standard from child or parent
                        standardName: child.standardName || standard.standardName || 'Standard', // ✅ FIX: Include standardName
                        productCategory: child.productCategoryId?.toString() || '1',
                        productCategoryName: child.productCategoryName || 'Product Category',
                        serviceItem: child.serviceItemId?.toString() || '1',
                        serviceItemName: child.serviceItemName || 'Service Item',
                        yearType: child.yearType || 0,
                        selectedYear: child.selectedYear || new Date().getFullYear(),
                        qty: child.qty || 1,
                        unit: child.unit || '1',
                        proposeDate: child.proposeDate || '',
                        workflowIdSystem: child.workflowIdSystem || null,
                        workflowIdChoice: child.workflowIdChoice || null
                      }))
                  }))
              }));
              
              setProposalServices(updatedServices);
            }
          }
          
          // ✅ TAMBAHAN: Reset deleted data setelah save berhasil
          setDeletedServices([]);
          setDeletedStandards([]);
          setDeletedChildren([]);
      } else {
        // POST (create) - buat project baru
        const createResponse = await api.post('/crm/projects', payload);
        savedProjectId = createResponse.data.id;
          
          // ✅ Update project data dengan code yang di-generate backend
          if (createResponse.data.projectCode) {
            setProposalData(prev => ({
              ...prev,
              id: createResponse.data.id || savedProjectId,
              proposalCode: createResponse.data.projectCode
            }));
          }
          
          // ✅ TAMBAHAN: Reset deleted data setelah create berhasil
          setDeletedServices([]);
          setDeletedStandards([]);
          setDeletedChildren([]);
      }

      // Sites are managed in Proposal → Audit Methodology section, not saved here.

      // ✅ PERBAIKAN: Template processing untuk project
      if (proposalData.projectTemplateId && savedProjectId) {
        try {
          // Download dan fill template (DOCX)
          const filledDocxBlob = await downloadAndFillTemplate(proposalData.projectTemplateId, savedProjectId);
          
          // Upload DOCX ter-fill untuk project
          await uploadFilledDocument(filledDocxBlob, savedProjectId);
          
          setAlert({ 
            type: 'success', 
            message: 'Project saved successfully! Template filled, DOCX uploaded, and PDF generated automatically.' 
          });
          
        } catch (templateError) {
          console.error('Error processing project template:', templateError);
          setAlert({ 
            type: 'warning', 
            message: 'Project saved successfully! But failed to process template.' 
          });
          return; // Keluar dari fungsi jika template error
        }
      } else {
        // Jika tidak ada template, hanya tampilkan success message
        setAlert({ 
          type: 'success', 
          message: 'Project saved successfully!' 
        });
      }
      
      // ✅ PERBAIKAN: Navigate ke project detail
      navigate(`/projects/detail/${id}`);
    } catch (err) {
      console.error('Error saving project:', err);
      setAlert({ 
        type: 'danger', 
        message: 'Failed to save project: ' + getApiErrorMessage(err)
      });
    } finally {
      setLoading(false);
    }
  };





  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const res = await api.get('/crm/services/options');
        setProductTypes(res.data);
      } catch (err) {
        console.error('fetchProductTypes error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProductTypes();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // TAMBAHAN: Cek apakah data sudah di-fetch
      if (dataFetched) return;
      
      try {
        // ✅ PERBAIKAN: Coba fetch dari project edit endpoint terlebih dahulu
        try {
          const projectRes = await api.get(`/crm/projects/edit/lead/${id}`);
          
          if (projectRes.data) {
            // Map project data ke format yang sesuai
            const project = projectRes.data;
            
            // Convert projectServices to project_sections format (same as DetailProject)
            const project_sections = [];
            
            if (Array.isArray(project.projectServices) && project.projectServices.length > 0) {
              project.projectServices.forEach((service, sectionIndex) => {
                const standards = Array.isArray(service.standards) ? service.standards : [];
                
                if (standards.length > 0) {
                  const mappedServices = [];
                  
                  standards.forEach((std) => {
                    const mapChild = (child) => ({
                      id: child.id,
                      // ✅ PERBAIKAN: Store IDs untuk mapping ke frontend
                      productCategoryId: child.productCategoryId,
                      productCategoryName: child.productCategoryName || 'Product Category',
                      serviceItemId: child.serviceItemId,
                      serviceItemName: child.serviceItemName || 'Service Item',
                      yearType: child.yearType || 0,
                      selectedYear: child.selectedYear || new Date().getFullYear(),
                      qty: child.qty || 1,
                      unit: mapUnitNameToId(child.unit),
                      unitPrice: child.unitPrice || 0,
                      proposeDate: child.proposeDate || '',
                      workflowIdSystem: child.workflowIdSystem || null,
                      workflowIdChoice: child.workflowIdChoice || null
                    });

                    mappedServices.push({
                      id: std.id,
                      // ✅ PERBAIKAN: Store IDs untuk mapping ke frontend
                      standardId: std.standardId,
                      standardName: std.standardName || 'Standard',
                      productCategoryId: std.productCategoryId,
                      productCategoryName: std.productCategoryName || 'Product Category',
                      serviceItemId: std.serviceItemId,
                      serviceItemName: std.serviceItemName || 'Service Item',
                      yearType: std.yearType || 0,
                      selectedYear: std.selectedYear || new Date().getFullYear(),
                      qty: std.qty || 1,
                      unit: std.unit || '1',
                      unitPrice: std.unitPrice || 0,
                      proposeDate: std.proposeDate || '',
                      workflowIdSystem: std.workflowIdSystem || null,
                      workflowIdChoice: std.workflowIdChoice || null,
                      // ✅ PERBAIKAN: Map children ke sub_services seperti DetailProject
                      sub_services: Array.isArray(std.children) ? std.children.map(mapChild) : []
                    });
                  });
                  
                    project_sections.push({
                      id: service.id || (sectionIndex + 1),
                      type: sectionIndex === 0 ? 'Services' : 'Additional Services',
              serviceName: service.serviceName || 'Service',
                      // ✅ PERBAIKAN: Store serviceId untuk mapping ke frontend
                      serviceId: service.serviceId,
                      standards: mappedServices
                    });
                } else {
                  // Fallback: no standards provided, still show the service line
                  project_sections.push({
                    id: service.id || (sectionIndex + 1),
                    type: sectionIndex === 0 ? 'Services' : 'Additional Services',
                    serviceName: service.serviceName || 'Service',
                    // ✅ PERBAIKAN: Store serviceId untuk mapping ke frontend
                    serviceId: service.serviceId,
                    standards: [{
                      id: Date.now(),
                      standardId: 1,
                      standardName: 'Standard',
                      productCategoryId: 1,
                      productCategoryName: 'Product Category',
                      serviceItemId: 1,
                      serviceItemName: 'Service Item',
                      yearType: 0,
                      selectedYear: new Date().getFullYear(),
                      qty: 1,
                      unit: '1',
                      unitPrice: 0,
                      proposeDate: '',
                      workflowIdSystem: null,
                      workflowIdChoice: null,
                      productCategories: []
                    }]
                  });
                }
              });
            }
            
            // ✅ PERBAIKAN: Convert project_sections to EditProject format yang sesuai dengan DetailProject
            const services = project_sections.map((section, sectionIndex) => ({
              id: section.id || Date.now(),
              // ✅ PERBAIKAN: Convert serviceId ke string untuk AppSelect
              service: section.serviceId?.toString() || (sectionIndex + 1).toString(),
              serviceName: section.serviceName || 'Service',
              // ✅ PERBAIKAN: Map standards ke format yang sesuai dengan DetailProject
              standards: (section.standards || []).map(standard => ({
                  id: standard.id,
                // ✅ PERBAIKAN: Convert standardId ke string untuk AppSelect
                  standard: standard.standardId?.toString() || '1',
                  standardName: standard.standardName || 'Standard',
                // ✅ PERBAIKAN: Convert productCategoryId ke string untuk AppSelect
                  productCategory: standard.productCategoryId?.toString() || '1',
                  productCategoryName: standard.productCategoryName || 'Product Category',
                // ✅ PERBAIKAN: Convert serviceItemId ke string untuk AppSelect
                  serviceItem: standard.serviceItemId?.toString() || '1',
                  serviceItemName: standard.serviceItemName || 'Service Item',
                  yearType: standard.yearType || 0,
                  selectedYear: standard.selectedYear || new Date().getFullYear(),
                  qty: standard.qty || 1,
                  unit: mapUnitNameToId(standard.unit),
                  unitPrice: standard.unitPrice || 0,
                  proposeDate: standard.proposeDate || '',
                  workflowIdSystem: standard.workflowIdSystem || null,
                  workflowIdChoice: standard.workflowIdChoice || null,
                // ✅ PERBAIKAN: Map sub_services (children) ke productCategories untuk EditProject
                productCategories: (standard.sub_services || []).map(child => ({
                      id: child.id,
                  // ✅ FIX: Include standard from child or parent for proper saving
                      standard: child.standardId?.toString() || standard.standardId?.toString() || '1',
                      standardName: child.standardName || standard.standardName || 'Standard',
                  // ✅ PERBAIKAN: Convert productCategoryId ke string untuk AppSelect
                      productCategory: child.productCategoryId?.toString() || '1',
                      productCategoryName: child.productCategoryName || 'Product Category',
                  // ✅ PERBAIKAN: Convert serviceItemId ke string untuk AppSelect
                      serviceItem: child.serviceItemId?.toString() || '1',
                      serviceItemName: child.serviceItemName || 'Service Item',
                      yearType: child.yearType || 0,
                      selectedYear: child.selectedYear || new Date().getFullYear(),
                      qty: child.qty || 1,
                      unit: mapUnitNameToId(child.unit),
                      unitPrice: child.unitPrice || 0,
                      proposeDate: child.proposeDate || '',
                      workflowIdSystem: child.workflowIdSystem || null,
                      workflowIdChoice: child.workflowIdChoice || null
                    }))
                }))
            }));
            
            setProposalServices(services);
            setProposalData(prev => ({
              ...prev,
              id: project.id,
              companyName: project.companyName || '',
              proposalCode: project.projectCode || '',
              projectDate: project.projectDate || '',
              validUntil: project.validUntil || '',
              partnerType: project.partnerType || '',
              assignedTo: project.assignedTo || '',
              team: project.team || '',
              projectTemplateId: project.projectTemplateId || null,
              multiSite: Boolean(project.multiSite),
              auditFlaggingType: project.auditFlaggingType || 'none',
              auditMethodology: project.auditMethodology || '',
              integratedStandardIds: project.integratedStandardIds || [],
              combinedStandardIds: project.combinedStandardIds || [],
              auditConfigurationCompleted: Boolean(project.auditConfigurationCompleted),
              rootStandardCount: project.rootStandardCount || 0,
            }));
            loadProjectSites(project.id);

            setDataFetched(true);
            return;
          }
        } catch (projectError) {
          // Fallback to detail endpoint
          try {
            const projectRes = await api.get(`/crm/projects/detail/lead/${id}`);
            
            if (projectRes.data) {
              // Use the same mapping logic as above but with the original endpoint structure
              const project = projectRes.data;
              
              // Convert projectServices to project_sections format (same as DetailProject)
              const project_sections = [];
              
              if (Array.isArray(project.projectServices) && project.projectServices.length > 0) {
                project.projectServices.forEach((service, sectionIndex) => {
                  const standards = Array.isArray(service.standards) ? service.standards : [];
                  
                  if (standards.length > 0) {
                    const mappedServices = [];
                    
                    standards.forEach((std) => {
                      const mapChild = (child) => ({
                        id: child.id,
                        // ✅ PERBAIKAN: Store IDs untuk mapping ke frontend
                        productCategoryId: child.productCategoryId,
                        productCategoryName: child.productCategoryName || 'Product Category',
                        serviceItemId: child.serviceItemId,
                        serviceItemName: child.serviceItemName || 'Service Item',
                        yearType: child.yearType || 0,
                        selectedYear: child.selectedYear || new Date().getFullYear(),
                        qty: child.qty || 1,
                        unit: child.unit || '1',
                        unitPrice: child.unitPrice || 0,
                        proposeDate: child.proposeDate || '',
                        workflowIdSystem: child.workflowIdSystem || null,
                        workflowIdChoice: child.workflowIdChoice || null
                      });

                      mappedServices.push({
                        id: std.id,
                        // ✅ PERBAIKAN: Store IDs untuk mapping ke frontend
                        standardId: std.standardId,
                        standardName: std.standardName || 'Standard',
                        productCategoryId: std.productCategoryId,
                        productCategoryName: std.productCategoryName || 'Product Category',
                        serviceItemId: std.serviceItemId,
                        serviceItemName: std.serviceItemName || 'Service Item',
                        yearType: std.yearType || 0,
                        selectedYear: std.selectedYear || new Date().getFullYear(),
                        qty: std.qty || 1,
                        unit: std.unit || '1',
                        unitPrice: std.unitPrice || 0,
                        proposeDate: std.proposeDate || '',
                        workflowIdSystem: std.workflowIdSystem || null,
                        workflowIdChoice: std.workflowIdChoice || null,
                        // ✅ PERBAIKAN: Map children ke sub_services seperti DetailProject
                        sub_services: Array.isArray(std.children) ? std.children.map(mapChild) : []
                      });
                    });
                    
                    project_sections.push({
                      id: service.id || (sectionIndex + 1),
                      type: sectionIndex === 0 ? 'Services' : 'Additional Services',
                      serviceName: service.serviceName || 'Service',
                      // ✅ PERBAIKAN: Store serviceId untuk mapping ke frontend
                      serviceId: service.serviceId,
                      standards: mappedServices
                    });
                  } else {
                  // Fallback: no standards provided, still show the service line
                  project_sections.push({
                    id: service.id || (sectionIndex + 1),
                    type: sectionIndex === 0 ? 'Services' : 'Additional Services',
                    serviceName: service.serviceName || 'Service',
                    // ✅ PERBAIKAN: Store serviceId untuk mapping ke frontend
                    serviceId: service.serviceId,
                    standards: [{
                      id: Date.now(),
                      standardId: 1,
                      standardName: 'Standard',
                      productCategoryId: 1,
                      productCategoryName: 'Product Category',
                      serviceItemId: 1,
                      serviceItemName: 'Service Item',
                      yearType: 0,
                      selectedYear: new Date().getFullYear(),
                      qty: 1,
                      unit: '1',
                      unitPrice: 0,
                      proposeDate: '',
                      workflowIdSystem: null,
                      workflowIdChoice: null,
                      productCategories: []
                    }]
                  });
                  }
                });
              }
              
              // ✅ PERBAIKAN: Convert project_sections to EditProject format yang sesuai dengan DetailProject
              const services = project_sections.map((section, sectionIndex) => ({
                id: section.id || Date.now(),
                // ✅ PERBAIKAN: Convert serviceId ke string untuk AppSelect
                service: section.serviceId?.toString() || (sectionIndex + 1).toString(),
                serviceName: section.serviceName || 'Service',
                // ✅ PERBAIKAN: Map standards ke format yang sesuai dengan DetailProject
                standards: (section.standards || []).map(standard => ({
                  id: standard.id,
                  // ✅ PERBAIKAN: Convert standardId ke string untuk AppSelect
                  standard: standard.standardId?.toString() || '1',
                  standardName: standard.standardName || 'Standard',
                  // ✅ PERBAIKAN: Convert productCategoryId ke string untuk AppSelect
                  productCategory: standard.productCategoryId?.toString() || '1',
                  productCategoryName: standard.productCategoryName || 'Product Category',
                  // ✅ PERBAIKAN: Convert serviceItemId ke string untuk AppSelect
                  serviceItem: standard.serviceItemId?.toString() || '1',
                  serviceItemName: standard.serviceItemName || 'Service Item',
                  yearType: standard.yearType || 0,
                  selectedYear: standard.selectedYear || new Date().getFullYear(),
                  qty: standard.qty || 1,
                  unit: mapUnitNameToId(standard.unit),
                  unitPrice: standard.unitPrice || 0,
                  proposeDate: standard.proposeDate || '',
                  workflowIdSystem: standard.workflowIdSystem || null,
                  workflowIdChoice: standard.workflowIdChoice || null,
                // ✅ PERBAIKAN: Map sub_services (children) ke productCategories untuk EditProject
                productCategories: (standard.sub_services || []).map(child => ({
                  id: child.id,
                  // ✅ FIX: Include standard from child or parent for proper saving
                  standard: child.standardId?.toString() || standard.standardId?.toString() || '1',
                  standardName: child.standardName || standard.standardName || 'Standard',
                  // ✅ PERBAIKAN: Convert productCategoryId ke string untuk AppSelect
                  productCategory: child.productCategoryId?.toString() || '1',
                  productCategoryName: child.productCategoryName || 'Product Category',
                  // ✅ PERBAIKAN: Convert serviceItemId ke string untuk AppSelect
                  serviceItem: child.serviceItemId?.toString() || '1',
                  serviceItemName: child.serviceItemName || 'Service Item',
                      yearType: child.yearType || 0,
                      selectedYear: child.selectedYear || new Date().getFullYear(),
                      qty: child.qty || 1,
                      unit: mapUnitNameToId(child.unit),
                      unitPrice: child.unitPrice || 0,
                      proposeDate: child.proposeDate || '',
                      workflowIdSystem: child.workflowIdSystem || null,
                      workflowIdChoice: child.workflowIdChoice || null
                    }))
                }))
            }));
            
            setProposalServices(services);
              setProposalData(prev => ({
                ...prev,
                id: project.id,
                companyName: project.companyName || '',
              proposalCode: project.projectCode || '',
                projectDate: project.projectDate || '',
              validUntil: project.validUntil || '',
              partnerType: project.partnerType || '',
              assignedTo: project.assignedTo || '',
              team: project.team || '',
              projectTemplateId: project.projectTemplateId || null,
              multiSite: Boolean(project.multiSite),
              auditFlaggingType: project.auditFlaggingType || 'none',
              auditMethodology: project.auditMethodology || '',
              integratedStandardIds: project.integratedStandardIds || [],
              combinedStandardIds: project.combinedStandardIds || [],
              auditConfigurationCompleted: Boolean(project.auditConfigurationCompleted),
              rootStandardCount: project.rootStandardCount || 0,
              }));
              loadProjectSites(project.id);
            
            setDataFetched(true);
            return;
          }
          } catch (originalError) {
            // Continue to proposal fallback
          }
        }
        
        // Fallback ke proposal data jika project tidak ditemukan
        const proposalRes = await api.get(`/crm/proposals/detail/lead/${id}`);
        
        if (proposalRes.data) {
          
          // Map proposal data ke format project
          const proposal = proposalRes.data;
          
          // ✅ PERBAIKAN: Map proposal sections ke format project services (maksimal 2 services)
          const services = (proposal.proposal_sections || []).slice(0, 2).map((section, sectionIndex) => ({
            id: section.id || Date.now(),
            // ✅ PERBAIKAN: Gunakan service ID yang benar dari proposal data
            service: sectionIndex === 0 ? '1' : '2', // Set ID untuk Services/Additional Services
            serviceName: section.type || 'Service',
            standards: (section.services || [])
              .filter(service => typeof service === 'object' && service !== null)
              .slice(0, 1)
              .map(service => ({
                id: service.id || Date.now(), // ✅ TAMBAHAN: Include ID untuk tracking
                // ✅ PERBAIKAN: Gunakan ID yang benar dari proposal data, bukan hardcoded '1'
                standard: '1', // Set ID untuk standard dropdown
                standardName: service.standard || 'Standard',
                productCategory: '1', // Set ID untuk product category dropdown  
                productCategoryName: service.product_type || 'Product Category',
                serviceItem: '1', // Set ID untuk service item dropdown
                serviceItemName: service.service_item || 'Service Item',
                qty: service.qty || 1,
                unit: '1', // Set ID untuk unit dropdown
                proposeDate: service.proposeDate || '',
                // GANTI sub_services → productCategories
                productCategories: service.sub_services
                  ? service.sub_services.map(subService => ({
                      id: subService.id || Date.now(), // ✅ TAMBAHAN: Include ID untuk tracking
                      // ✅ PERBAIKAN: Gunakan ID yang benar dari proposal sub-services data
                      productCategory: subService.productCategoryId?.toString() || '1', // Use actual productCategoryId
                      productCategoryName: subService.product_type || 'Product Category',
                      serviceItem: subService.serviceItemId?.toString() || '1', // Use actual serviceItemId
                      serviceItemName: subService.service_item || 'Service Item',
                      qty: subService.qty || 1,
                      unit: mapUnitNameToId(subService.unit),
                      proposeDate: subService.proposeDate || ''
                    }))
                  : []
              }))
          }));
          
          setProposalServices(services);
          setProposalData(prev => ({
            ...prev,
            companyName: proposal.client?.name || proposal.companyName || '',
            proposalCode: proposal.proposal_number || proposal.proposalCode || '',
            proposalDate: proposal.dates?.proposal_date || proposal.proposalDate || '',
            validUntil: proposal.dates?.valid_until || proposal.validUntil || '',
            partnerType: proposal.partnerType || '',
            assignedTo: proposal.assigned_to?.name || '',
            team: proposal.assigned_to?.team || '',
            projectTemplateId: proposal.template?.id || null
          }));
          
          setDataFetched(true);
          return;
        }
        
        // Fallback: Show error if all APIs fail
        setAlert({
          type: 'danger',
          message: 'Failed to load project data. Please try again later.'
        });
        setDataFetched(true);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setAlert({
          type: 'danger',
          message: 'Failed to load project data: ' + (err.response?.data?.message || err.message)
        });
        setDataFetched(true);
      } finally {
        setLoading(false);
      }
    };

    if (id && !dataFetched) fetchData(); // TAMBAHAN: Cek flag
  }, [id, dataFetched]); // TAMBAHAN: Tambahkan dataFetched ke dependency

  useEffect(() => {
    if (productTypes.length) {
      if (leadProducts.length === 0) {
        setProposalData(prev => ({
          ...prev,
          serviceType: '',
          services: [],
        }));
      } else {
        const mainProduct = leadProducts[0];
        const additional = leadProducts.slice(1);
        setProposalData(prev => ({
          ...prev,
          serviceType: String(mainProduct?.serviceId ?? ''),
          services: [], // or map as needed
        }));
      }
    }
  }, [leadProducts, productTypes]);

  useEffect(() => {
    setProposalData(prev => ({
      ...prev,
      leadId: id
    }));
  }, [id]);

  // Fetch standards and categories for each section when service changes
  useEffect(() => {
    
    proposalServices.forEach((section, idx) => {
      if (section.service) {
        // Fetch standards
        api.get(`/md/standards/by-service/${section.service}`)
          .then(res => {
            setStandardOptionsBySection(prev => ({
              ...prev,
              [idx]: res.data.map(item => ({ id: item.value || item.id, name: item.label || item.name }))
            }));
          })
          .catch(() => {
            setStandardOptionsBySection(prev => ({ ...prev, [idx]: [] }));
          });
        // Fetch product categories
        api.get(`/md/product-category/by-service/${section.service}`)
          .then(res => {
            setCategoryOptionsBySection(prev => ({
              ...prev,
              [idx]: res.data.map(item => ({ id: item.id, name: item.name }))
            }));
          })
          .catch(() => {
            setCategoryOptionsBySection(prev => ({ ...prev, [idx]: [] }));
          });
      } else {
        setStandardOptionsBySection(prev => ({ ...prev, [idx]: [] }));
        setCategoryOptionsBySection(prev => ({ ...prev, [idx]: [] }));
      }
    });
  }, [proposalServices.map(s => s.service).join(',')]);

  // Fetch items for each row and sub-row when product category changes
  useEffect(() => {
    proposalServices.forEach((section, sectionIdx) => {
      // Main rows
      section.standards.forEach((standard, standardIdx) => {
        if (section.service && standard.productCategory) {
          api.get('/md/audit-type/by-service-and-category', {
            params: {
              serviceId: section.service,
              productCategory: standard.productCategory
            }
          })
            .then(res => {
              setItemOptionsBySection(prev => ({
                ...prev,
                [`${sectionIdx}_${standardIdx}`]: res.data.map(item => ({ id: item.id, name: item.name }))
              }));
            })
            .catch((error) => {
              console.error('Error fetching item options:', error);
              setItemOptionsBySection(prev => ({ ...prev, [`${sectionIdx}_${standardIdx}`]: [] }));
            });
        } else {
          setItemOptionsBySection(prev => ({ ...prev, [`${sectionIdx}_${standardIdx}`]: [] }));
        }
        // Sub-rows (children)
        if (standard.productCategories && Array.isArray(standard.productCategories)) {
          standard.productCategories.forEach((productCategory, productCategoryIdx) => {
            if (section.service && productCategory.productCategory) {
              api.get('/md/audit-type/by-service-and-category', {
                params: {
                  serviceId: section.service,
                  productCategory: productCategory.productCategory
                }
              })
                .then(res => {
                  setItemOptionsBySection(prev => ({
                    ...prev,
                    [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: res.data.map(item => ({ id: item.id, name: item.name }))
                  }));
                })
                .catch((error) => {
                  console.error('Error fetching sub-item options:', error);
                  setItemOptionsBySection(prev => ({ ...prev, [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: [] }));
                });
            } else {
              setItemOptionsBySection(prev => ({ ...prev, [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: [] }));
            }
          });
        }
      });
    });
  }, [proposalServices.map(section => section.standards.map(standard => standard.productCategory + (standard.productCategories ? standard.productCategories.map(pc => pc.productCategory).join(',') : '')).join(',')).join('|'), proposalServices.map(s => s.service).join(',')]);














  // ✅ PERBAIKAN: Update fungsi uploadFilledDocument untuk upload ke project
  const uploadFilledDocument = async (fileBlob, projectId) => {
    try {
      const formData = new FormData();
      formData.append('file', fileBlob, `project_${projectId}_filled.docx`);
      
      const response = await api.post(
        `/crm/projects/${projectId}/upload-filled-document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error uploading filled document to project:', error);
      throw error;
    }
  };

  // ✅ PERBAIKAN: Fungsi untuk download file ter-fill yang sudah tersimpan untuk project
  const downloadSavedFilledDocument = async (projectId) => {
    try {
      const response = await api.get(`/crm/projects/${projectId}/download-filled-document`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${projectId}_filled.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading filled project document:', error);
      alert('Failed to download filled project document');
    }
  };

  if (loading) {
    return <Loading />;
  }





  const auditProcessCta = createAuditProcessCtaState({
    projectId: proposalData.id,
    auditConfigurationCompleted: proposalData.auditConfigurationCompleted,
    creatingAuditProcess,
  });

  return (
    <>
      {loading && <Loading />}
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}
      


      <div className="container-xl">
        <ProjectHeader
          companyName={proposalData.companyName}
          proposalCode={proposalData.proposalCode}
          handleBack={() => window.history.length > 1 ? navigate(-1) : navigate('/projects')}
          projectDate={proposalData.projectDate}
          validUntil={proposalData.validUntil}
          handleDateChange={(e) => {
            setProposalData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
          }}
          // ✅ TAMBAHAN: Props untuk project
          isProject={true}
          partnerType={proposalData.partnerType}
          assignedTo={proposalData.assignedTo}
          team={proposalData.team}
          onPartnerTypeChange={(e) =>
            setProposalData((prev) => ({ ...prev, partnerType: e.target.value }))
          }
          onAssignedToChange={(e) =>
            setProposalData((prev) => ({ ...prev, assignedTo: e.target.value }))
          }
          onTeamChange={(e) =>
            setProposalData((prev) => ({ ...prev, team: e.target.value }))
          }
        />

        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title mb-0">Audit Configuration</h3>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Multi-site</label>
                <div className="mt-1">
                  {proposalData.multiSite ? (
                    <span className="badge bg-teal text-white">
                      Multi-site{proposalData.numberOfSites ? ` (${proposalData.numberOfSites} sites)` : ''}
                    </span>
                  ) : (
                    <span className="badge bg-secondary">Single site</span>
                  )}
                </div>
                <div className="form-hint mt-1">Set in the Proposal → Audit Methodology.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Audit Methodology</label>
                <div className="mt-1">
                  {proposalData.auditMethodology ? (
                    <span className="badge bg-blue text-white fs-6 px-3 py-2">{proposalData.auditMethodology}</span>
                  ) : (
                    <span className="badge bg-secondary fs-6 px-3 py-2">Not set</span>
                  )}
                </div>
                <div className="form-hint mt-1">
                  Set in the Proposal. Projects with more than one standard must use Integrated or Combined audit before Audit Platform tasks can be created.
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Configuration status</label>
                <div>
                  <span className={`badge ${proposalData.auditConfigurationCompleted ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {proposalData.auditConfigurationCompleted ? 'Ready for Audit Process' : 'Needs review before Audit Process'}
                  </span>
                </div>
              </div>
            </div>

            {(proposalData.auditFlaggingType === 'integrated' || proposalData.auditFlaggingType === 'combined') && (
              <div className="mt-3">
                <label className="form-label">Select standards for {proposalData.auditFlaggingType} audit</label>
                <div className="form-hint mb-2">
                  Integrated audit uses the same auditor for multiple standards on the same audit date. Combined audit uses different auditors in the same audit window.
                </div>
                {getAuditStandardOptions().length > 1 ? (
                  <div className="row g-2">
                    {getAuditStandardOptions().map(option => {
                      const targetField = proposalData.auditFlaggingType === 'integrated' ? 'integratedStandardIds' : 'combinedStandardIds';
                      const selected = (proposalData[targetField] || []).map(Number).includes(Number(option.id));
                      return (
                        <div className="col-md-6" key={`${option.id}-${option.label}`}>
                          <label className="form-check border rounded p-2 h-100">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => updateAuditStandardSelection(targetField, option.id, e.target.checked)}
                            />
                            <span className="form-check-label">
                              {option.label}
                              <span className="text-muted ms-1">({option.serviceName})</span>
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="alert alert-warning mb-0">
                    Integrated/combined audit can only be configured after the project has more than one standard.
                  </div>
                )}
              </div>
            )}

            {/* PRD Audit #5: explicit Create Audit Process CTA. Disabled until audit
                configuration is complete; on click POSTs the create endpoint. */}
            <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateAuditProcess}
                disabled={auditProcessCta.disabled}
                title={auditProcessCta.title}
              >
                {creatingAuditProcess ? 'Creating Audit Process…' : 'Create Audit Process'}
              </button>
              <small className="text-muted">
                {auditProcessCta.helper}
              </small>
            </div>

            {proposalData.multiSite && (
              <div className="alert alert-info mt-3 mb-0 py-2">
                <small>Site details are managed in the <strong>Proposal → Audit Methodology</strong> section.</small>
              </div>
            )}
          </div>
        </div>
        {/* Render service sections */}
        {proposalServices.map((section, idx) => (
          <div key={section.id} className="card mb-4 position-relative">
            <button
              type="button"
              className="btn btn-icon btn-danger position-absolute end-0 top-0 m-2"
              aria-label="Delete Section"
              onClick={() => handleDeleteSection(idx)}
              style={{ zIndex: 2 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
            </button>
                         <div className="card-body">
               <label className="form-label">
                 {idx === 0 ? 'Services' : 'Additional Services'}
               </label>
               <div className="col-md-3 mb-3">
                <AppSelect
                  name={`service-${section.id}`}
                  value={section.service}
                  onChange={(name, value) => {
                    const updatedSections = [...proposalServices];
                    updatedSections[idx].service = value;
                    setProposalServices(updatedSections);
                  }}
                  options={serviceOptions}
                  placeholder="Select Service"
                  className="mb-2"
                  dropdownParent="body"
                />
              </div>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Standard</th>
                      <th style={{ minWidth: 200 }}>Product Category</th>
                      <th style={{ minWidth: 200 }}>Service Item</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Audit Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.standards.map((standard, standardIdx) => (
                      <React.Fragment key={standardIdx}>
                        <tr>
                          <td style={{ minWidth: 150 }}>
                            <AppSelect
                              name={`standard-${section.id}-${standardIdx}`}
                              value={standard.standard}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].standard = value;
                                // ✅ TAMBAHAN: Update standardName juga berdasarkan pilihan user
                                const selectedOption = standardOptionsBySection[idx]?.find(opt => opt.id === value);
                                if (selectedOption) {
                                  updatedSections[idx].standards[standardIdx].standardName = selectedOption.name;
                                }
                                setProposalServices(updatedSections);
                              }}
                              options={standardOptionsBySection[idx] || []}
                              placeholder="Select Standard"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <AppSelect
                              name={`product-category-${section.id}-${standardIdx}`}
                              value={standard.productCategory}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].productCategory = value;
                                // ✅ FIX: Update productCategoryName based on selected option
                                const selectedOption = categoryOptionsBySection[idx]?.find(opt => opt.id === value);
                                if (selectedOption) {
                                  updatedSections[idx].standards[standardIdx].productCategoryName = selectedOption.name;
                                }
                                setProposalServices(updatedSections);
                              }}
                              options={categoryOptionsBySection[idx] || []}
                              placeholder="Select Product Category"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <AppSelect
                              name={`item-${section.id}-${standardIdx}`}
                              value={standard.serviceItem}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].serviceItem = value;
                                // ✅ FIX: Update serviceItemName based on selected option
                                const selectedOption = itemOptionsBySection[`${idx}_${standardIdx}`]?.find(opt => opt.id === value);
                                if (selectedOption) {
                                  updatedSections[idx].standards[standardIdx].serviceItemName = selectedOption.name;
                                }
                                setProposalServices(updatedSections);
                              }}
                              options={itemOptionsBySection[`${idx}_${standardIdx}`] || []}
                              placeholder="Select Service Item"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 80 }}>
                            <input type="number" className="form-control" value={standard.qty} onChange={e => {
                              const updatedSections = [...proposalServices];
                              updatedSections[idx].standards[standardIdx].qty = e.target.value;
                              setProposalServices(updatedSections);
                            }} />
                          </td>
                          <td style={{ minWidth: 100 }}>
                            <AppSelect
                              name={`unit-${section.id}-${standardIdx}`}
                              value={standard.unit}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].unit = value;
                                setProposalServices(updatedSections);
                              }}
                              options={unitOptions}
                              placeholder="Select Unit"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <input
                              type="date"
                              className="form-control"
                              name={`proposeDate-${section.id}-${standardIdx}`}
                              value={standard.proposeDate || ''}
                              disabled={categoryOptionsBySection[idx]?.find(opt => opt.id === standard.productCategory)?.name === 'CMF'}
                              onChange={(e) => {
                                const updatedSections = [...proposalServices];
                                const newDate = e.target.value;
                                updatedSections[idx].standards[standardIdx].proposeDate = newDate;
                                
                                // Auto-fill +1 year for next row if this row is non-CMF and has a date
                                if (newDate && standardIdx < updatedSections[idx].standards.length - 1) {
                                  const currentStandardCategory = categoryOptionsBySection[idx]?.find(opt => opt.id === standard.productCategory)?.name;
                                  if (currentStandardCategory !== 'CMF') {
                                    const nextStandard = updatedSections[idx].standards[standardIdx + 1];
                                    const nextStandardCategory = categoryOptionsBySection[idx]?.find(opt => opt.id === nextStandard.productCategory)?.name;
                                    
                                    // Only auto-fill if next row is also non-CMF and empty
                                    if (nextStandardCategory !== 'CMF' && !nextStandard.proposeDate) {
                                      const currentDate = new Date(newDate);
                                      currentDate.setFullYear(currentDate.getFullYear() + 1);
                                      nextStandard.proposeDate = currentDate.toISOString().split('T')[0];
                                    }
                                  }
                                }
                                
                                setProposalServices(updatedSections);
                              }}
                            />
                          </td>
                          <td>
                            <button className="btn btn-icon btn-danger" onClick={() => handleDeleteStandard(idx, standardIdx)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
                            </button>
                          </td>
                        </tr>
                        {standard.productCategories && standard.productCategories.map((productCategory, productCategoryIdx) => (
                          <tr key={`product-category-${standardIdx}-${productCategoryIdx}`} className="table-light">
                            <td className="text-end pe-2">
                              <small className="text-muted">↳</small>
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <AppSelect
                                name={`product-category-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.productCategory}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].productCategory = value;
                                  // ✅ FIX: Update productCategoryName based on selected option
                                  const selectedOption = categoryOptionsBySection[idx]?.find(opt => opt.id === value);
                                  if (selectedOption) {
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].productCategoryName = selectedOption.name;
                                  }
                                  setProposalServices(updatedSections);
                                }}
                                options={categoryOptionsBySection[idx] || []}
                                placeholder="Select Product Category"
                                dropdownParent="body"
                              />
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <AppSelect
                                name={`item-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.serviceItem}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].serviceItem = value;
                                  // ✅ FIX: Update serviceItemName based on selected option
                                  const selectedOption = itemOptionsBySection[`sub_${idx}_${standardIdx}_${productCategoryIdx}`]?.find(opt => opt.id === value);
                                  if (selectedOption) {
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].serviceItemName = selectedOption.name;
                                  }
                                  setProposalServices(updatedSections);
                                }}
                                options={itemOptionsBySection[`sub_${idx}_${standardIdx}_${productCategoryIdx}`] || []}
                                placeholder="Select Service Item"
                                dropdownParent="body"
                              />
                            </td>
                            <td style={{ minWidth: 80 }}>
                              <input type="number" className="form-control" value={productCategory.qty} onChange={e => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].qty = e.target.value;
                                setProposalServices(updatedSections);
                              }} />
                            </td>
                            <td style={{ minWidth: 100 }}>
                              <AppSelect
                                name={`unit-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.unit}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].unit = value;
                                  setProposalServices(updatedSections);
                                }}
                                options={unitOptions}
                                placeholder="Select Unit"
                                dropdownParent="body"
                              />
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <input
                                type="date"
                                className="form-control"
                                name={`proposeDate-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.proposeDate || ''}
                                disabled={categoryOptionsBySection[idx]?.find(opt => opt.id === productCategory.productCategory)?.name === 'CMF'}
                                onChange={(e) => {
                                  const updatedSections = [...proposalServices];
                                  const newDate = e.target.value;
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].proposeDate = newDate;
                                  
                                  // Auto-fill +1 year for next product category if this one is non-CMF and has a date
                                  if (newDate && productCategoryIdx < updatedSections[idx].standards[standardIdx].productCategories.length - 1) {
                                    const currentCategory = categoryOptionsBySection[idx]?.find(opt => opt.id === productCategory.productCategory)?.name;
                                    if (currentCategory !== 'CMF') {
                                      const nextProductCategory = updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx + 1];
                                      const nextCategory = categoryOptionsBySection[idx]?.find(opt => opt.id === nextProductCategory.productCategory)?.name;
                                      
                                      // Only auto-fill if next product category is also non-CMF and empty
                                      if (nextCategory !== 'CMF' && !nextProductCategory.proposeDate) {
                                        const currentDate = new Date(newDate);
                                        currentDate.setFullYear(currentDate.getFullYear() + 1);
                                        nextProductCategory.proposeDate = currentDate.toISOString().split('T')[0];
                                      }
                                    }
                                  }
                                  
                                  setProposalServices(updatedSections);
                                }}
                              />
                            </td>
                            <td>
                              <button className="btn btn-icon btn-danger" onClick={() => handleDeleteProductCategory(idx, standardIdx, productCategoryIdx)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {/* Button: Add Product Category (was Add Service Category) */}
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="d-flex justify-content-end pe-4">
                              <button
                                className="btn btn-icon btn-primary"
                                type="button"
                                title="Add Product Category"
                                onClick={() => handleAddProductCategory(idx, standardIdx)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {/* Button: Add Standard (was Add Row) */}
                <button
                  className="btn btn-primary mt-2"
                  type="button"
                  onClick={() => handleAddStandard(idx)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                  Add Standard
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {/* Add Service button - moved to right side below service sections */}
        <div className="d-flex justify-content-end my-3">
          <button className="btn btn-primary" onClick={handleAddService}>
            + Add Service
          </button>
        </div>
        
            <div className="d-flex justify-content-end mt-3 mb-3 me-3">
          <button className="btn btn-outline-primary me-2" onClick={() => navigate('/project')}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Project'}
          </button>
        </div>
        
      </div>
    </>
  );
};

export default EditProject;
